import { ChartColors, PROPERTY_NAME, PROPERTY_VALUE, TYPE_KEY } from "../common/constant";
import ObjectUtils, { coalesce } from "../utils/ObjectUtils";
import { beginLoading, endLoading } from "./loadingSpinner";
import { createBackendErrorMessages, createErrorMessages, createWarningMessages, messagesReceived } from "./message";

import { faChartLine, faChartPie, faColumns } from "@fortawesome/free-solid-svg-icons";
import FileSaver from "file-saver";
import _ from "lodash";
import rest from "../common/rest";
import PDFCreator from "../component/user/PDFCreator";
import { getProgramInstanceFacetParameterString } from "../utils/ApiUtils";
import { getSubQuestionModifiedId } from "../utils/TableUtils";

const { getDeepProperty } = ObjectUtils;

const PROPERTY_CHANGED_ACTION = "CHARTS.PROPERTY_CHANGED_ACTION";
const UPDATE_SINGLE_QUESTION_ACTION = "CHARTS.UPDATE_SINGLE_QUESTION_ACTION";
const REQUESTED_QUESTION_KEY = "requestedQuestionKey";
const LOAD_TABLE_DETAILS_ACTION = "CHARTS.LOAD_TABLE_DETAILS_ACTION";
const LOAD_MODAL_SELECT_PARAMETERS = "CHARTS.LOAD_MODAL_SELECT_PARAMETERS";
const CLEAR_MODAL_SELECT_PARAMETERS = "CHARTS.CLEAR_MODAL_SELECT_PARAMETERS";
const LOAD_MODAL_SELECT_QUESTION_LIST = "CHARTS.LOAD_MODAL_SELECT_QUESTION_LIST";
const REMOVE_TABLE_DETAILS_ACTION = "CHARTS.REMOVE_TABLE_DETAILS_ACTION";
const REMOVE_TABLE_SUBQUESTION_DETAILS_ACTION = "CHARTS.REMOVE_TABLE_SUBQUESTION_DETAILS_ACTION";
const LOAD_TABLE_SUBQUESTION_DETAILS_ACTION = "CHARTS.LOAD_TABLE_SUBQUESTION_DETAILS_ACTION";
const SET_QUESTION_GROUPS_ACTION = "CHARTS.SET_QUESTION_GROUPS_ACTION";
const LOAD_SURVEY_SUBMISSIONS_ACTION = "CHARTS.LOAD_SURVEY_SUBMISSIONS_ACTION";
const LOAD_TEMPLATE_ACTION = "CHARTS.LOAD_TEMPLATE_ACTION";
const REMOVE_PDF_CONVERSION_DATA = "REMOVE.PDF.CONVERSION.DATA";
const SET_SINCE_BEGINNING = "CHARTS.SET_SINCE_BEGINNING";
const CLEAR_SINCE_BEGINNING = "CHARTS.CLEAR_SINCE_BEGINNING";
export const CURRENT_ACTIVE_PAGE = "currentActivePage";
export const SECTIONS = "sections";
export const COLLAPSIBLE_TABLES_DETAILS = "collapsibleTablesDetails";
export const COLLAPSIBLE_TABLES_SUBQUESTION_DETAILS = "collapsibleTablesSubquestionDetails";
export const QUESTION_GROUPS = "questionGroups";
export const MISSING_QUESTION_GROUPS = "missingQuestionGroups";
export const PROJECT_COUNT = "projectCount";
export const LATEST_SURVEY_TEMPLATE_NAME = "latestSurveyTemplateName";
export const LATEST_SURVEY_TEMPLATE_VERSION = "latestSurveyTemplateVersion";
export const SURVEYS_BY_REPORTING_YEAR = "surveysByReportingYear";
export const QUESTION_LIST = "questionList";
export const MODAL_SELECT_QUESTION_LIST = "modalSelectQuestionList";
export const MODAL_SELECT_PARAMETERS = "modalSelectParameters";
export const CSV_QUESTION_DETAILS = "csvQuestionDetails";
export const EXPANDED_QUESTION_LIST = "expandedQuestionList";
export const CHART_CONFIG = "chartConfig";
export const SURVEY_SUBMISSIONS = "surveySubmissions";
export const TEMPLATES = "templates";
export const START_CONVERSION = "startConversion";
export const PROGRAM_INSTANCE_FOREIGN_IDS = "programInstanceForeignIds";
export const PDF_DOWNLOAD_PRINT_CONFIG = "pdfDownloadPrintConfig";
export const SINCE_BEGINNING = "sinceBeginning";

export const TEXT = "TEXT";
export const COMMENT = "COMMENT";
export const DATE = "DATE";
export const NUMBER = "NUMBER";
export const DYNAMIC_TABLE = "DYNAMIC_TABLE";
export const STATIC_TABLE = "STATIC_TABLE";
export const MATRIX_SINGLE_CHOICE = "MATRIX_SINGLE_CHOICE";
export const MATRIX_MULTIPLE_CHOICE = "MATRIX_MULTIPLE_CHOICE";
export const TOTAL = "TOTAL";
export const PREDEFINED_LIST = "PREDEFINED_LIST";
export const RADIO = "RADIO";
export const CHECKBOX = "CHECKBOX";
export const DROPDOWN = "DROPDOWN";

export const CHART_MODE_TABLE = "TABLE";
export const CHART_MODE_CHART = "CHART";
export const CHART_TYPE_BAR = "BAR";
export const CHART_TYPE_COLUMN = "COLUMN";
export const CHART_TYPE_PIE = "PIE";
export const CHART_TYPE_LINE = "LINE";

export const initialState = {
  [CURRENT_ACTIVE_PAGE]: null,
  [QUESTION_GROUPS]: [],
  [MISSING_QUESTION_GROUPS]: [],
  [COLLAPSIBLE_TABLES_DETAILS]: [],
  [COLLAPSIBLE_TABLES_SUBQUESTION_DETAILS]: [],
  [QUESTION_LIST]: [],
  [MODAL_SELECT_QUESTION_LIST]: {},
  [MODAL_SELECT_PARAMETERS]: {},
  [CSV_QUESTION_DETAILS]: [],
  [EXPANDED_QUESTION_LIST]: [],
  [CHART_CONFIG]: {},
  [SURVEY_SUBMISSIONS]: [],
  [PROGRAM_INSTANCE_FOREIGN_IDS]: [],
  [TEMPLATES]: {},
  [START_CONVERSION]: false,
  [LATEST_SURVEY_TEMPLATE_NAME]: null,
  [LATEST_SURVEY_TEMPLATE_VERSION]: null,
  [SURVEYS_BY_REPORTING_YEAR]: {},
  [SINCE_BEGINNING]: {}
};

export default function reducer(state = initialState, action) {
  let newState;
  switch (action[TYPE_KEY]) {
    case PROPERTY_CHANGED_ACTION:
      newState = Object.assign({}, state, {
        [action[PROPERTY_NAME]]: action[PROPERTY_VALUE]
      });
      break;
    case UPDATE_SINGLE_QUESTION_ACTION:
      const updatedQuestions = action[PROPERTY_VALUE] || [];
      newState = Object.assign({}, state, {
        [action[PROPERTY_NAME]]: updatedQuestions.length > 0
          ? state[QUESTION_LIST].map((question) =>
              findUpdatedQuestion(question, updatedQuestions, action[REQUESTED_QUESTION_KEY]) || question
            )
          : state[QUESTION_LIST]
      });
      break;
    case LOAD_TABLE_DETAILS_ACTION:
      newState = Object.assign({}, state, {
        [COLLAPSIBLE_TABLES_DETAILS]: addNewItem(state, action, COLLAPSIBLE_TABLES_DETAILS)
      });
      break;
    case LOAD_MODAL_SELECT_PARAMETERS:
      newState = Object.assign({}, state, {
        [MODAL_SELECT_PARAMETERS]: addNewItemToObject(state, action, MODAL_SELECT_PARAMETERS)
      });
      break;
    case CLEAR_MODAL_SELECT_PARAMETERS:
      newState = Object.assign({}, state, {
        [MODAL_SELECT_PARAMETERS]: action[PROPERTY_VALUE]
      });
      break;
    case SET_SINCE_BEGINNING:
      newState = Object.assign({}, state, {
        [SINCE_BEGINNING]: addNewItemToObject(state, action, SINCE_BEGINNING)
      });
      break;
    case CLEAR_SINCE_BEGINNING:
      newState = Object.assign({}, state, {
        [SINCE_BEGINNING]: {}
      });
      break;
    case LOAD_MODAL_SELECT_QUESTION_LIST:
      newState = Object.assign({}, state, {
        [MODAL_SELECT_QUESTION_LIST]: addNewItemToObject(state, action, MODAL_SELECT_QUESTION_LIST)
      });
      break;
    case LOAD_TABLE_SUBQUESTION_DETAILS_ACTION:
      newState = Object.assign({}, state, {
        [COLLAPSIBLE_TABLES_SUBQUESTION_DETAILS]: addNewItem(state, action, COLLAPSIBLE_TABLES_SUBQUESTION_DETAILS)
      });
      break;
    case REMOVE_TABLE_DETAILS_ACTION:
      newState = Object.assign({}, state, {
        [COLLAPSIBLE_TABLES_DETAILS]: action[PROPERTY_VALUE]
      });
      break;
    case REMOVE_TABLE_SUBQUESTION_DETAILS_ACTION:
      newState = Object.assign({}, state, {
        [COLLAPSIBLE_TABLES_SUBQUESTION_DETAILS]: action[PROPERTY_VALUE]
      });
      break;
    case SET_QUESTION_GROUPS_ACTION:
      newState = Object.assign({}, state, {
        [QUESTION_GROUPS]: action[PROPERTY_VALUE][QUESTION_GROUPS],
        [MISSING_QUESTION_GROUPS]: action[PROPERTY_VALUE][MISSING_QUESTION_GROUPS] || [],
        [PROJECT_COUNT]: action[PROPERTY_VALUE][PROJECT_COUNT],
        [LATEST_SURVEY_TEMPLATE_NAME]: action[PROPERTY_VALUE][LATEST_SURVEY_TEMPLATE_NAME],
        [LATEST_SURVEY_TEMPLATE_VERSION]: action[PROPERTY_VALUE][LATEST_SURVEY_TEMPLATE_VERSION],
        [SURVEYS_BY_REPORTING_YEAR]: action[PROPERTY_VALUE][SURVEYS_BY_REPORTING_YEAR]
      });
      break;
    case LOAD_SURVEY_SUBMISSIONS_ACTION:
      newState = Object.assign({}, state, {
        [SURVEY_SUBMISSIONS]: action[PROPERTY_VALUE],
        [TEMPLATES]: {},
        [START_CONVERSION]: false
      });
      break;
    case LOAD_TEMPLATE_ACTION:
      let obj = ObjectUtils.deepClone(coalesce(state[TEMPLATES], {}));
      let key = action[PROPERTY_VALUE].identifier + "=" + action[PROPERTY_VALUE].surveyTemplate?.version;
      let val = action[PROPERTY_VALUE];
      obj[key] = val;
      newState = Object.assign({}, state, {
        [TEMPLATES]: obj
      });

      if (Object.keys(obj).length === Object.keys(state[SURVEY_SUBMISSIONS].templates).length) {
        newState = Object.assign({}, state, {
          [START_CONVERSION]: true,
          [TEMPLATES]: obj
        });
      }

      break;
    case REMOVE_PDF_CONVERSION_DATA:
      newState = Object.assign({}, state, {
        [SURVEY_SUBMISSIONS]: [],
        [TEMPLATES]: {},
        [START_CONVERSION]: false
      });
      break;
    default:
      newState = Object.assign({}, state, {});
      break;
  }
  return newState;
}

function findUpdatedQuestion(question, updatedQuestions, requestedQuestionKey) {
  const updatedQuestion = updatedQuestions.find((candidate) => sameQuestion(question, candidate));
  if (updatedQuestion) {
    return updatedQuestion;
  }
  if (
    updatedQuestions.length === 1 &&
    requestedQuestionKey &&
    question.questionKey &&
    String(question.questionKey) === String(requestedQuestionKey)
  ) {
    return updatedQuestions[0];
  }
  return null;
}

function sameQuestion(question, updatedQuestion) {
  return (
    String(question.questionId) === String(updatedQuestion.questionId) ||
    (!!question.questionKey &&
      !!updatedQuestion.questionKey &&
      String(question.questionKey) === String(updatedQuestion.questionKey))
  );
}

/**
 * get the chart config from the database
 * @param chartType the type of chart
 * @returns {{}}
 */
export function getChartConfig() {
  return (dispatch) => {
    let config = {};
    config[CHART_TYPE_COLUMN] = {
      chartTitle: null,
      chartType: "ColumnChart",
      columns: null,
      height: "320px",
      icon: faColumns,
      options: {
        annotations: {
          textStyle: { fontSize: 11 },
          alwaysOutside: false
        },
        chartArea: {
          height: "55%",
          left: 75
        },
        colors: ChartColors,
        hAxis: {
          title: "Format",
          textStyle: { fontSize: 12 }
        },
        titleTextStyle: { fontSize: 16 },
        vAxis: { title: "Anzahl" }
      },
      rows: null,
      width: "918px"
    };
    config[CHART_TYPE_PIE] = {
      chartTitle: null,
      chartType: "PieChart",
      columns: null,
      height: "320px",
      icon: faChartPie,
      options: {
        chartArea: {
          width: "100%",
          left: 50,
          top: 100
        },
        annotations: {
          textStyle: { fontSize: 11 },
          alwaysOutside: false
        },
        colors: ChartColors,
        hAxis: {
          title: "Format",
          textStyle: { fontSize: 12 }
        },
        pieSliceText: "value",
        titleTextStyle: { fontSize: 16 }
      },
      rows: null,
      width: "450px"
    };
    config[CHART_TYPE_BAR] = {
      chartTitle: null,
      chartType: "BarChart",
      columns: null,
      height: "600px",
      icon: faColumns,
      options: {
        annotations: {
          textStyle: { fontSize: 11 },
          alwaysOutside: false
        },
        chartArea: {
          height: "75%",
          left: 75
        },
        colors: ChartColors,
        hAxis: {
          title: "Format",
          textStyle: { fontSize: 12 }
        },
        titleTextStyle: { fontSize: 16 },
        vAxis: { title: "Anzahl" }
      },
      rows: null,
      width: "918px"
    };
    config[CHART_TYPE_LINE] = {
      chartTitle: null,
      chartType: "LineChart",
      columns: null,
      height: "320px",
      icon: faChartLine,
      options: {
        annotations: {
          textStyle: { fontSize: 11 },
          alwaysOutside: false
        },
        chartArea: {
          height: "55%",
          left: 75
        },
        colors: ChartColors,
        hAxis: {
          title: "Format",
          textStyle: { fontSize: 12 }
        },
        titleTextStyle: { fontSize: 16 },
        vAxis: { title: "Anzahl" }
      },
      rows: null,
      width: "918px"
    };
    dispatch(propertyChange(CHART_CONFIG, config));
  };
}

/**
 * Load question groups from server - left container in component
 */
export function loadQuestionGroupData(
  questionGroupKey,
  questionKeys,
  status,
  selectedTimeFacets,
  questionKey,
  evaluateSinceFundingStarted
) {
  return (dispatch, getState) => {
    let selectedFundingAreaRefId = getState().fundingArea.selectedFundingArea.indicatorsModalDialog.id;
    let selectedProgram = getState().fundingArea.selectedProgram;
    let selectedProgramRefId =
      selectedProgram && selectedProgram.indicatorsModalDialog ? selectedProgram.indicatorsModalDialog.id : null;
    let selectedProgramInstance = getState().fundingArea.selectedProgramInstance;
    let programInstanceRefId =
      selectedProgramInstance && selectedProgramInstance.indicatorsModalDialog
        ? selectedProgramInstance.indicatorsModalDialog.id
        : null;

    dispatch(beginLoading());

    let url = `/survey/evaluations/frequency/frequencyAnalysisByProgram?fundingAreaRefId=${selectedFundingAreaRefId}&questionGroupKey=${questionGroupKey}`;
    if (questionKeys && questionKeys.length > 0) {
      url += getQuestionKeysParameterString(questionKeys);
    }
    if (selectedProgramRefId) {
      url += `&programRefId=${selectedProgramRefId}`;
    }
    if (programInstanceRefId) {
      url += `&instanceRefId=${programInstanceRefId}`;
    } else {
      const instanceRefIds = getState().collapsibleTableChart.programInstanceForeignIds;
      url += getProgramInstanceFacetParameterString(instanceRefIds);
    }
    if (status) {
      url += `&status=${status}`;
    }
    if (questionKey) {
        url += `&questionId=${questionKey}`;
        url += `&questionKey=${encodeURIComponent(questionKey)}`;
    }
    if (evaluateSinceFundingStarted) {
      url += `&evaluateSinceFundingStarted=${evaluateSinceFundingStarted}`;
    }

    if (selectedTimeFacets) {
      url += getTimeFacetParameterString(selectedTimeFacets);
    }

    rest.doGet(url).then(
      (json) => {
        if (json) {
          dispatch(propertyChange(QUESTION_LIST, json[QUESTION_LIST], questionKey));
          dispatch(propertyChange(CURRENT_ACTIVE_PAGE, questionGroupKey));
          dispatch(endLoading());
        } else {
          dispatch(messagesReceived(createErrorMessages(json)));
          dispatch(endLoading());
        }
      },
      (reason) => {
        dispatch(messagesReceived(createBackendErrorMessages(reason)));
        dispatch(endLoading());
      }
    );
  };
}

/**
 * Load question groups from server - left container in component
 */
export function loadQuestionGroupDataModal(
  questionGroupKey,
  questionKeys,
  status,
  selectedTimeFacets,
  activeQuestion,
  activeModalQuestionId
) {
  return (dispatch, getState) => {
    let selectedFundingAreaRefId = getState().fundingArea.selectedFundingArea.indicatorsModalDialog.id;
    let selectedProgram = getState().fundingArea.selectedProgram;
    let selectedProgramRefId =
      selectedProgram && selectedProgram.indicatorsModalDialog ? selectedProgram.indicatorsModalDialog.id : null;
    let selectedProgramInstance = getState().fundingArea.selectedProgramInstance;
    let programInstanceRefId =
      selectedProgramInstance && selectedProgramInstance.indicatorsModalDialog
        ? selectedProgramInstance.indicatorsModalDialog.id
        : null;

    dispatch(beginLoading());

    let url = `/survey/evaluations/frequency/pivotStructureByQuestionGroup?fundingAreaRefId=${selectedFundingAreaRefId}&questionGroupKey=${questionGroupKey}`;
    if (questionKeys && questionKeys.length > 0) {
      url += getQuestionKeysParameterString(questionKeys);
    }
    if (selectedProgramRefId) {
      url += `&programRefId=${selectedProgramRefId}`;
    }
    if (programInstanceRefId) {
      url += `&instanceRefId=${programInstanceRefId}`;
    } else {
      const instanceRefIds = getState().collapsibleTableChart.programInstanceForeignIds;
      url += getProgramInstanceFacetParameterString(instanceRefIds);
    }
    if (status) {
      url += `&status=${status}`;
    }

    if (selectedTimeFacets) {
      url += getTimeFacetParameterString(selectedTimeFacets);
    }

    rest.doGet(url).then(
      (json) => {
        if (json) {
          dispatch(setModalQuestions(reduceQuestionList(json[QUESTION_LIST], activeQuestion), activeModalQuestionId));
          dispatch(endLoading());
        } else {
          dispatch(messagesReceived(createErrorMessages(json)));
          dispatch(endLoading());
        }
      },
      (reason) => {
        dispatch(messagesReceived(createBackendErrorMessages(reason)));
        dispatch(endLoading());
      }
    );
  };
}

function reduceQuestionList(questionList, activeQuestion) {
  let returnList = [];
  let questionType = activeQuestion.questionType;
  switch (questionType) {
    case DYNAMIC_TABLE:
      returnList = questionList
        .filter((element) => element.questionId === activeQuestion.questionId || element.questionType !== DYNAMIC_TABLE)
        .filter((element) => element.questionType !== COMMENT)
        .filter((element) => element.questionType !== TEXT);
      break;
    case NUMBER:
      returnList = questionList
        .filter((element) => element.questionType !== NUMBER)
        .filter((element) => element.questionType !== COMMENT)
        .filter((element) => element.questionType !== TEXT);
      break;
    default:
      returnList = questionList;
      break;
  }
  return returnList;
}

/**
 * @param state
 * @param action
 * @param type
 * @returns {list} - represents list with the keys which are questionIds in this case
 */
function addNewItem(state, action, type) {
  let list = ObjectUtils.deepClone(coalesce(state[type], []));
  let key = action[PROPERTY_NAME];
  let val = action[PROPERTY_VALUE];
  list.push({ [key]: val });
  return list;
}

/**
 * @param state
 * @param action
 * @param type
 * @returns {list} - represents list with the keys which are questionIds in this case
 */
function addNewItemToObject(state, action, type) {
  let obj = ObjectUtils.deepClone(coalesce(state[type], {}));
  let key = action[PROPERTY_NAME];
  let val = action[PROPERTY_VALUE];
  obj[key] = val;
  return obj;
}

/**
 * Change Property Action
 */
export function propertyChange(propertyName, propertyValue, questionKey) {
  if (questionKey) {
    return {
      [TYPE_KEY]: UPDATE_SINGLE_QUESTION_ACTION,
      [PROPERTY_NAME]: propertyName,
      [PROPERTY_VALUE]: propertyValue,
      [REQUESTED_QUESTION_KEY]: questionKey
    };
  } else {
    return {
      [TYPE_KEY]: PROPERTY_CHANGED_ACTION,
      [PROPERTY_NAME]: propertyName,
      [PROPERTY_VALUE]: propertyValue
    };
  }
}

/**
 * Add an id to list of expanded tables (static and dynamic type)
 */
export function expandComplexTable(questionID) {
  return (dispatch, getState) => {
    let expandedList = ObjectUtils.deepClone(getState().collapsibleTableChart.expandedQuestionList);
    expandedList.push(questionID);
    dispatch(propertyChange(EXPANDED_QUESTION_LIST, expandedList));
  };
}

/**
 * Remove an id from list of expanded tables (static and dynamic type)
 */
export function collapseComplexTable(questionID) {
  return (dispatch, getState) => {
    let expandedList = ObjectUtils.deepClone(coalesce(getState().collapsibleTableChart.expandedQuestionList, []));
    let expandedListObjectIndex = getObjectFromArray(expandedList, questionID);
    expandedList.splice(expandedListObjectIndex, 1);

    dispatch(propertyChange(EXPANDED_QUESTION_LIST, expandedList));
  };
}

/**
 * Expanded list has keys with questionId value - so we are checking here does this questionId belongs to the list
 */
function getObjectFromArray(expandedList, questionID) {
  let index = -1;
  for (let i = 0; i < expandedList.length; i++) {
    if (expandedList[i] === questionID) {
      index = i;
      break;
    }
  }
  return index;
}

/**
 * Load details for table from server - expanded state
 *
 * @param questionID
 * @param status
 * @param selectedTimeFacets
 * @param groupByQuestionKey
 * @param groupByOptionKey
 * @param groupByColumnKey
 * @param groupByRowKey
 * @returns {function(*, *): void}
 */
export function loadTableDetails(
  questionID,
  questionKey,
  questionGroupKey,
  columnKey,
  rowKey,
  status,
  selectedTimeFacets,
  groupByQuestionKey,
  groupByColumnKey,
  groupByRowKey,
  latest
) {
  return (dispatch, getState) => {
    dispatch(beginLoading());

    let selectedFundingAreaRefId = getState().fundingArea.selectedFundingArea.indicatorsModalDialog.id;
    let selectedProgram = getState().fundingArea.selectedProgram;
    let selectedProgramRefId =
      selectedProgram && selectedProgram.indicatorsModalDialog ? selectedProgram.indicatorsModalDialog.id : null;
    let selectedProgramInstance = getState().fundingArea.selectedProgramInstance;
    let programInstanceRefId =
      selectedProgramInstance && selectedProgramInstance.indicatorsModalDialog
        ? selectedProgramInstance.indicatorsModalDialog.id
        : null;

    let url = latest
      ? `/survey/evaluations/frequency/frequencyAnalysisByProjectLatest?fundingAreaRefId=${selectedFundingAreaRefId}`
      : `/survey/evaluations/frequency/frequencyAnalysisByProject?fundingAreaRefId=${selectedFundingAreaRefId}`;
    if (selectedProgramRefId) {
      url += `&programRefId=${selectedProgramRefId}`;
    }
    if (programInstanceRefId) {
      url += `&instanceRefId=${programInstanceRefId}`;
    } else {
      const instanceRefIds = getState().collapsibleTableChart.programInstanceForeignIds;
      url += getProgramInstanceFacetParameterString(instanceRefIds);
    }
    if (status) {
      url += `&status=${status}`;
    }
    if (questionID || questionID === 0) {
      url += `&questionId=${questionID}`;
    }
    if (questionKey) {
      url += `&questionKey=${encodeURIComponent(questionKey)}`;
    }
    if (questionGroupKey) {
      url += `&questionGroupKey=${questionGroupKey}`;
    }
    if (columnKey) {
      url += `&columnKey=${columnKey}`;
    }
    if (rowKey) {
      url += `&rowKey=${rowKey}`;
    }
    // pivot table options
    if (groupByQuestionKey) {
      url += `&groupByQuestionKey=${groupByQuestionKey}`;
    } else {
      url += `&groupByQuestionKey=projectData`;
    }
    if (groupByColumnKey) {
      url += `&groupByColumnKey=${groupByColumnKey}`;
    }
    if (groupByRowKey) {
      url += `&groupByRowKey=${groupByRowKey}`;
    }
    if (selectedTimeFacets) {
      url += getTimeFacetParameterString(selectedTimeFacets);
    }

    rest.doGet(url).then(
      (json) => {
        if (json.questionList.length > 0) {
          dispatch(propertyChange(CSV_QUESTION_DETAILS, json.questionList));
          dispatch(endLoading());
        } else {
          dispatch(messagesReceived(createErrorMessages(json)));
          dispatch(endLoading());
        }
      },
      (reason) => {
        dispatch(messagesReceived(createBackendErrorMessages(reason)));
        dispatch(endLoading());
      }
    );
  };
}

export function removeCsvQuestionDetails() {
  return (dispatch) => {
    dispatch(propertyChange(CSV_QUESTION_DETAILS, []));
  };
}

/**
 * Load details for table from server - expanded state
 *
 * @param questionID
 * @param colID
 * @param rowID
 * @param columnType - we will get different structure of json for static and for dynamic
 * @param status
 * @param selectedTimeFacets
 * @param groupByQuestionKey
 * @param groupByOptionKey
 * @param groupByColumnKey
 * @param groupByRowKey
 * @param latest
 * @param staticTableRows
 * @returns {function(*, *): void}
 */
export function loadTableSubquestionDetails(
  questionID,
  questionKey,
  colID,
  rowID,
  columnKey,
  rowKey,
  questionGroupkey,
  identifier,
  columnType,
  questionType,
  status,
  selectedTimeFacets,
  groupByQuestionKey,
  groupByQuestionType,
  groupByColumnType,
  groupByColumnKey,
  groupByRowKey,
  latest,
  staticTableRows
) {
  return (dispatch, getState) => {
    let subquestionId = getSubQuestionModifiedId(questionID, identifier, rowID, colID);
    dispatch(beginLoading());
    let selectedFundingAreaRefId = getState().fundingArea.selectedFundingArea.indicatorsModalDialog.id;
    let selectedProgram = getState().fundingArea.selectedProgram;
    let selectedProgramRefId =
      selectedProgram && selectedProgram.indicatorsModalDialog ? selectedProgram.indicatorsModalDialog.id : null;
    let selectedProgramInstance = getState().fundingArea.selectedProgramInstance;
    let programInstanceRefId =
      selectedProgramInstance && selectedProgramInstance.indicatorsModalDialog
        ? selectedProgramInstance.indicatorsModalDialog.id
        : null;

    // matrix has two types (single and multiple) but just one query
    const queryType = questionType.includes("MATRIX") ? "matrix" : questionType;

    let url = latest
      ? `/survey/evaluations/frequency/frequencyAnalysisByProject_${queryType}_Latest?fundingAreaRefId=${selectedFundingAreaRefId}`
      : `/survey/evaluations/frequency/frequencyAnalysisByProject_${queryType}?fundingAreaRefId=${selectedFundingAreaRefId}`;
    if (selectedProgramRefId) {
      url += `&programRefId=${selectedProgramRefId}`;
    }
    if (programInstanceRefId) {
      url += `&instanceRefId=${programInstanceRefId}`;
    } else {
      const instanceRefIds = getState().collapsibleTableChart.programInstanceForeignIds;
      url += getProgramInstanceFacetParameterString(instanceRefIds);
    }
    if (status) {
      url += `&status=${status}`;
    }
    if (questionID || questionID === 0) {
      url += `&questionId=${questionID}`;
    }
    if (questionKey) {
      url += `&questionKey=${questionKey}`;
    }
    if (columnKey) {
      url += `&columnKey=${columnKey}`;
    }
    if (rowKey) {
      url += `&rowKey=${rowKey}`;
    }
    if (questionGroupkey) {
      url += `&questionGroupkey=${questionGroupkey}`;
    }
    if (questionType) {
      url += `&questionType=${questionType}`;
    }
    if (columnType) {
      url += `&columnType=${columnType}`;
    }
    if (colID || colID === 0) {
      url += `&colId=${colID}`;
    }
    if (rowID || rowID === 0) {
      url += `&rowId=${rowID}`;
    }
    // pivot table options
    if (groupByQuestionKey) {
      url += `&groupByQuestionKey=${groupByQuestionKey}`;
    } else {
      url += `&groupByQuestionKey=projectData`;
    }
    if (groupByQuestionType) {
      url += `&groupByQuestionType=${groupByQuestionType}`;
    }
    if (groupByColumnKey) {
      url += `&groupByColumnKey=${groupByColumnKey}`;
    }
    if (groupByColumnType) {
      url += `&groupByColumnType=${groupByColumnType}`;
    }
    if (groupByRowKey) {
      url += `&groupByRowKey=${groupByRowKey}`;
    }
    if (selectedTimeFacets) {
      url += getTimeFacetParameterString(selectedTimeFacets);
    }

    rest.doGet(url).then(
      (json) => {
        if (json.questionList.length > 0) {
          questionType === "STATIC_TABLE"
            ? dispatch(staticTableDetailsLoaded(json.questionList, subquestionId, staticTableRows))
            : dispatch(dynamicTableDetailsLoaded(json.questionList, subquestionId));
          dispatch(endLoading());
        } else {
          dispatch(messagesReceived(createErrorMessages(json)));
          dispatch(endLoading());
        }
      },
      (reason) => {
        dispatch(messagesReceived(createBackendErrorMessages(reason)));
        dispatch(endLoading());
      }
    );
  };
}

/**
 * Prepare setting of data for static table to state
 * @param question
 * @param subquestionId
 * @returns {function(*)}
 */
function staticTableDetailsLoaded(question, subquestionId, staticTableRows) {
  const questionType = question[0].anwser.anwserList[0].anwserList[0].questionType;
  return (dispatch) => {
    dispatch({
      [TYPE_KEY]: LOAD_TABLE_DETAILS_ACTION,
      [PROPERTY_NAME]: subquestionId,
      [PROPERTY_VALUE]:
        questionType === NUMBER
          ? calculateDuplicateStaticNumber(question[0].anwser.anwserList, staticTableRows)
          : question[0].anwser.anwserList[0].anwserList[0].data
    });
  };
}

/**
 * Prepare setting of data for dynamic table to state
 * @param question
 * @param subquestionId
 * @returns {function(*)}
 */
function dynamicTableDetailsLoaded(question, subquestionId) {
  const questionType = question[0].anwser.anwserList[0].questionType;
  return (dispatch) => {
    dispatch({
      [TYPE_KEY]: LOAD_TABLE_DETAILS_ACTION,
      [PROPERTY_NAME]: subquestionId,
      [PROPERTY_VALUE]:
        questionType === NUMBER
          ? calculateDuplicate(question[0].anwser.anwserList[0].data)
          : question[0].anwser.anwserList[0].data
    });
  };
}

/**
 * Modal data clearing
 * @param id
 * @returns {function(*)}
 */
export function clearModalParameter(id) {
  return (dispatch, getState) => {
    let parameters = {};
    if (id || id === 0) {
      parameters = ObjectUtils.deepClone(coalesce(getState().collapsibleTableChart.modalSelectParameters, []));
      delete parameters[id];
    }

    dispatch({
      [TYPE_KEY]: CLEAR_MODAL_SELECT_PARAMETERS,
      [PROPERTY_VALUE]: parameters
    });
  };
}

/**
 * Modal data setting
 * @param parameter
 * @param id
 * @returns {function(*)}
 */
export function setModalParameter(parameter, id) {
  return (dispatch) => {
    dispatch({
      [TYPE_KEY]: LOAD_MODAL_SELECT_PARAMETERS,
      [PROPERTY_NAME]: id,
      [PROPERTY_VALUE]: parameter
    });
  };
}

/**
 * Modal questions setting
 * @param parameter
 * @param id
 * @returns {function(*)}
 */
export function setModalQuestions(questions, id) {
  return (dispatch) => {
    dispatch({
      [TYPE_KEY]: LOAD_MODAL_SELECT_QUESTION_LIST,
      [PROPERTY_NAME]: id,
      [PROPERTY_VALUE]: questions
    });
  };
}

/**
 *
 *
 * @param questionId
 * @param value
 */
export function setSinceBeginning(questionId, value) {
  return (dispatch) => {
    dispatch({
      [TYPE_KEY]: SET_SINCE_BEGINNING,
      [PROPERTY_NAME]: questionId,
      [PROPERTY_VALUE]: value
    });
  };
}

export function clearSinceBeginning() {
  return (dispatch) => {
    dispatch({
      [TYPE_KEY]: CLEAR_SINCE_BEGINNING
    });
  };
}

/**
 * Function calculate duplicated ID's value
 * @param data
 * @returns {list with compressed data}
 */
function calculateDuplicate(data) {
  const res = _(data)
    .groupBy("zaxisValue")
    .map((obj, key) => ({
      zaxisValue: key,
      xaxisValue: _.sumBy(obj, (o) => parseInt(o.xaxisValue)),
      yaxisValue: _.sumBy(obj, (o) => parseInt(o.yaxisValue)),
      zaxisInfo: obj[0].zaxisInfo,
      answerCount: obj[0].answerCount
    }))
    .value();
  return res;
}

/**
 * Function calculate duplicated ID's value for numbers in static tables
 * @param data the data object
 * @param staticTableRows indicates if we are in "rows" view for static tables
 * @returns {list with compressed data}
 */
function calculateDuplicateStaticNumber(data, staticTableRows) {
  // different form in rows or columns
  const res = staticTableRows
    ? data[0].anwserList.map((obj, key) => {
        return calculateDuplicate(obj.data);
      })
    : data.map((obj, key) => {
        return calculateDuplicate(obj.anwserList[0].data);
      });
  return res;
}

/**
 * Remove details of table from state - collapsed state
 * @param questionID
 * @returns {function(*, *)}
 */
export function removeTableDetails(questionID) {
  return (dispatch, getState) => {
    let list = [];
    if (questionID || questionID === 0) {
      list = ObjectUtils.deepClone(coalesce(getState().collapsibleTableChart.collapsibleTablesDetails, []));
      let searchedObjectIndex = getObjectFromList(list, questionID);
      if (searchedObjectIndex >= 0) {
        list.splice(searchedObjectIndex, 1);
      }
    }

    dispatch({
      [TYPE_KEY]: REMOVE_TABLE_DETAILS_ACTION,
      [PROPERTY_VALUE]: list
    });
  };
}

/**
 * Return object from list
 * @param listOfObjects
 * @param nameOfKey
 * @returns {number}
 */
function getObjectFromList(listOfObjects, nameOfKey) {
  let searchedObjectIndex = -1;
  for (let i = 0; i < listOfObjects.length; i++) {
    if (listOfObjects[i][nameOfKey]) {
      searchedObjectIndex = i;
      break;
    }
  }
  return searchedObjectIndex;
}

/**
 * Load details for table
 * @param subquestionName
 * @returns {function(*, *)}
 */
export function loadTableQuestionDetails(subquestionName) {
  return (dispatch, getState) => {
    let subquestion = getState().collapsibleTableChart.mockedTableSubquestionData[subquestionName];
    dispatch({
      [TYPE_KEY]: LOAD_TABLE_SUBQUESTION_DETAILS_ACTION,
      [PROPERTY_NAME]: subquestionName,
      [PROPERTY_VALUE]: subquestion
    });
  };
}

/**
 * Remove expanded details of table
 * @param subquestionName
 * @returns {function(*, *)}
 */
export function removeTableQuestionDetails(subquestionName) {
  return (dispatch, getState) => {
    let list = ObjectUtils.deepClone(
      coalesce(getState().collapsibleTableChart.collapsibleTablesSubquestionDetails, [])
    );
    let searchedObjectIndex = getObjectFromList(list, subquestionName);
    list.splice(searchedObjectIndex, 1);
    dispatch({
      [TYPE_KEY]: REMOVE_TABLE_SUBQUESTION_DETAILS_ACTION,
      [PROPERTY_VALUE]: list
    });
  };
}

/**
 * Load Question groups - gets id of selected program, or from selected program instance if it is selected, and gets
 * groups of questions from server
 * @param programRefId
 * @param programInstanceRefId
 * @param status
 * @param selectedTimeFacets
 * @returns {function(*)}
 */
export function loadQuestionGroups(fundingAreaRefId, programRefId, programInstanceRefId, status, selectedTimeFacets) {
  return (dispatch, getState) => {
    dispatch(beginLoading());

    let url = `/survey/questionGroup/?fundingAreaRefId=${fundingAreaRefId}`;
    if (programRefId) {
      url += `&programRefId=${programRefId}`;
    }
    if (programInstanceRefId) {
      url += `&instanceRefId=${programInstanceRefId}`;
    } else {
      const instanceRefIds = getState().collapsibleTableChart.programInstanceForeignIds;
      url += getProgramInstanceFacetParameterString(instanceRefIds);
    }
    if (status) {
      url += `&status=${status}`;
    }
    if (selectedTimeFacets) {
      url += getTimeFacetParameterString(selectedTimeFacets);
    }
    rest.doGet(url).then(
      (json) => {
        if (json) {
          dispatch(loadSuccessful(json, SET_QUESTION_GROUPS_ACTION));
        } else {
          dispatch(messagesReceived(createErrorMessages(json)));
        }
        dispatch(endLoading());
      },
      (reason) => {
        dispatch(messagesReceived(createBackendErrorMessages(reason)));
        dispatch(endLoading());
      }
    );
  };
}

/**
 * Load textual question answers as csv.
 * @param questionID
 * @param colID
 * @param rowID
 * @param questionTitle
 * @returns {function(*, *)}
 */
export function downloadCSV(questionID, colID, rowID, questionTitle) {
  return (dispatch, getState) => {
    dispatch(beginLoading());
    const fundingArea = getState().fundingArea;
    let selectedFundingArea = fundingArea.selectedFundingArea;
    let selectedFundingAreaRefId =
      selectedFundingArea && selectedFundingArea.indicatorsModalDialog
        ? selectedFundingArea.indicatorsModalDialog.id
        : null;
    let selectedProgram = fundingArea.selectedProgram;
    let selectedProgramRefId =
      selectedProgram && selectedProgram.indicatorsModalDialog ? selectedProgram.indicatorsModalDialog.id : null;
    let selectedProgramInstance = fundingArea.selectedProgramInstance;
    let programInstanceRefId =
      selectedProgramInstance && selectedProgramInstance.indicatorsModalDialog
        ? selectedProgramInstance.indicatorsModalDialog.id
        : null;
    let url = `/survey/evaluations/frequency/programExportDynamicTable?fundingAreaRefId=${selectedFundingAreaRefId}`;
    if (selectedProgramRefId) {
      url += `&programRefId=${selectedProgramRefId}`;
    }
    if (programInstanceRefId) {
      url += `&instanceRefId=${programInstanceRefId}`;
    } else {
      const instanceRefIds = getState().collapsibleTableChart.programInstanceForeignIds;
      url += getProgramInstanceFacetParameterString(instanceRefIds);
    }

    if (questionID || questionID === 0) {
      url += `&questionId=${questionID}`;
    }
    if (colID || colID === 0) {
      url += `&colId=${colID}`;
    }
    if (rowID || rowID === 0) {
      url += `&rowId=${rowID}`;
    }
    rest.doGet(url, "text/csv").then(
      (response) => {
        try {
          let blob = new Blob(["\ufeff", response.response], { type: "text/csv;charset=utf-8" });
          let fileName = questionTitle.replace(/\s/g, "_").substring(0, 50) + new Date().getTime();
          FileSaver.saveAs(blob, fileName + ".csv");
          dispatch(endLoading());
        } catch (err) {
          dispatch(messagesReceived(createErrorMessages(err)));
          dispatch(endLoading());
        }
      },
      (reason) => {
        dispatch(messagesReceived(createBackendErrorMessages(reason)));
        dispatch(endLoading());
      }
    );
  };
}

export function downloadProgramCSV(
  status,
  selectedTimeFacets,
  type = "",
  questionTitle = "",
  questionID = undefined,
  zipDownload = undefined
) {
  return (dispatch, getState) => {
    dispatch(beginLoading());

    const fundingArea = getState().fundingArea;
    let selectedFundingArea = fundingArea.selectedFundingArea;
    let selectedFundingAreaRefId =
      selectedFundingArea && selectedFundingArea.indicatorsModalDialog
        ? selectedFundingArea.indicatorsModalDialog.id
        : null;
    let selectedProgram = fundingArea.selectedProgram;
    let selectedProgramRefId =
      selectedProgram && selectedProgram.indicatorsModalDialog ? selectedProgram.indicatorsModalDialog.id : null;
    let selectedProgramInstance = fundingArea.selectedProgramInstance;
    let programInstanceRefId =
      selectedProgramInstance && selectedProgramInstance.indicatorsModalDialog
        ? selectedProgramInstance.indicatorsModalDialog.id
        : null;

    if (selectedFundingAreaRefId) {
      let url = `/survey/evaluations/frequency`;
      if (zipDownload) {
        url += `?fundingAreaRefId=${selectedFundingAreaRefId}`;
      } else {
        url += `/programExport${type}?fundingAreaRefId=${selectedFundingAreaRefId}`;
      }

      if (selectedProgramRefId) {
        url += `&programRefId=${selectedProgramRefId}`;
      }
      if (programInstanceRefId) {
        url += `&instanceRefId=${programInstanceRefId}`;
      } else {
        const instanceRefIds = getState().collapsibleTableChart.programInstanceForeignIds;
        url += getProgramInstanceFacetParameterString(instanceRefIds);
      }

      if (questionID || questionID === 0) {
        url += `&questionId=${questionID}`;
        url += `&questionKey=${encodeURIComponent(questionID)}`;
      }
      if (status) {
        url += `&status=${status}`;
      }
      if (selectedTimeFacets) {
        url += getTimeFacetParameterString(selectedTimeFacets);
      }

      let programName = selectedProgramRefId
        ? selectedProgram.indicatorsModalDialog.name
        : selectedFundingArea.indicatorsModalDialog.name;
      if (zipDownload) {
        rest
          .doExport(url, "application/zip")
          .then((response) => response.blob())
          .then(
            (blob) => {
              try {
                let FileSaver = require("file-saver");
                let fileName = programName.replace(/\s/g, "_").substring(0, 50) + new Date().getTime();
                if (type !== "") {
                  fileName += "_" + type;
                }
                if (questionTitle !== "") {
                  fileName += "_" + questionTitle.substring(0, 25);
                }
                FileSaver.saveAs(blob, fileName + ".zip");
                dispatch(endLoading());
              } catch (err) {
                dispatch(messagesReceived(createErrorMessages(err)));
                dispatch(endLoading());
              }
            },
            (reason) => {
              dispatch(messagesReceived(createBackendErrorMessages(reason)));
              dispatch(endLoading());
            }
          );
      } else {
        rest.doGet(url, "text/csv").then(
          (response) => {
            try {
              let FileSaver = require("file-saver");
              let blob = new Blob(["\ufeff", response.response], { type: "text/csv;charset=utf-8" });
              let fileName = programName.replace(/\s/g, "_").substring(0, 50) + new Date().getTime();
              if (type !== "") {
                fileName += "_" + type;
              }
              if (questionTitle !== "") {
                fileName += "_" + questionTitle.substring(0, 25);
              }
              FileSaver.saveAs(blob, fileName + ".csv");
              dispatch(endLoading());
            } catch (err) {
              dispatch(messagesReceived(createErrorMessages(err)));
              dispatch(endLoading());
            }
          },
          (reason) => {
            dispatch(messagesReceived(createBackendErrorMessages(reason)));
            dispatch(endLoading());
          }
        );
      }
    } else {
      dispatch(endLoading());
    }
  };
}

export function downloadProjectsPdfArchive(status, selectedTimeFacets, finalReport) {
  return (dispatch, getState) => {
    const fundingArea = getState().fundingArea;
    let selectedFundingArea = fundingArea.selectedFundingArea;
    let selectedFundingAreaRefId =
      selectedFundingArea && selectedFundingArea.indicatorsModalDialog
        ? selectedFundingArea.indicatorsModalDialog.id
        : null;
    let selectedProgram = fundingArea.selectedProgram;
    let selectedProgramRefId =
      selectedProgram && selectedProgram.indicatorsModalDialog ? selectedProgram.indicatorsModalDialog.id : null;
    let selectedProgramInstance = fundingArea.selectedProgramInstance;
    let programInstanceRefId =
      selectedProgramInstance && selectedProgramInstance.indicatorsModalDialog
        ? selectedProgramInstance.indicatorsModalDialog.id
        : null;
    const instanceRefIds = getState().collapsibleTableChart.programInstanceForeignIds;

    dispatch(downloadSurveysPdfArchive(
      status,
      selectedTimeFacets,
      finalReport,
      selectedFundingAreaRefId,
      selectedProgramRefId,
      programInstanceRefId,
      instanceRefIds
    ));
  };
}

export function downloadSurveysPdfArchive(
  status,
  selectedTimeFacets,
  finalReport,
  selectedFundingAreaRefId,
  selectedProgramRefId,
  programInstanceRefId,
  instanceRefIds,
  selectedProjectRefId,
  regionKey,
  countryKey,
  submissionDateFrom,
  submissionDateTill,
) {
  return (dispatch, getState) => {
    dispatch(beginLoading());
    if (selectedFundingAreaRefId && selectedProgramRefId) {
      let url = `/survey/surveys/results?fundingAreaRefId=${selectedFundingAreaRefId}&programRefId=${selectedProgramRefId}`;

      if (programInstanceRefId) {
        url += `&instanceRefId=${programInstanceRefId}`;
      } else {
        url += getProgramInstanceFacetParameterString(instanceRefIds);
      }
      if (selectedProjectRefId) {
        url += `&projectRefId=${selectedProjectRefId}`;
      } else if (getDeepProperty(getState().fundingArea, "selectedProject", "overview", "id")) {
        url += `&projectRefId=${getState().fundingArea.selectedProject.overview.id}`;
      }

      if (status) {
        url += `&status=${status}`;
      }

      if (selectedTimeFacets) {
        url += getTimeFacetParameterString(selectedTimeFacets);
      }

      if (countryKey?.length) {
        url += `&targetCountry=${countryKey}`;
      }

      if (regionKey?.length) {
        url += `&targetRegion=${regionKey}`;
      }

      if (submissionDateFrom) {
        url += `&submissionDateFrom=${submissionDateFrom}`;
      }

      if (submissionDateTill) {
        url += `&submissionDateTill=${submissionDateTill}`;
      }

      url += `&finalReport=${finalReport ? "true" : "false"}`;

      rest.doGet(url).then(
        (json) => {
          if (json) {
            const keys = Object.keys(json.templates);
            if (keys.length) {
              keys.forEach((key) => {
                dispatch(loadSurveyTemplate(key.split("=")[0].substring(1), key.split("=")[1].replace("}", "")));
              });
              dispatch(loadSuccessful(json, LOAD_SURVEY_SUBMISSIONS_ACTION));
            } else {
              dispatch(messagesReceived(createWarningMessages("collapsible.no.reports.for.selection.warning")));
              dispatch(endLoading());
            }
          } else {
            dispatch(messagesReceived(createErrorMessages(json)));
          }
        },
        (reason) => {
          dispatch(messagesReceived(createBackendErrorMessages(reason)));
          dispatch(endLoading());
        }
      );
    } else {
      dispatch(endLoading());
    }
  };
}

export function loadSurveyTemplate(identifier, version) {
  return (dispatch) => {
    rest.doGet(`/admin/admin/surveys/${identifier}/version/${version}`).then(
      (json) => {
        if (json) {
          dispatch(loadSuccessful(json, LOAD_TEMPLATE_ACTION));
        } else {
          dispatch(messagesReceived(createErrorMessages(json)));
        }
      },
      (reason) => {
        dispatch(messagesReceived(createBackendErrorMessages(reason)));
      }
    );
  };
}

export function convertPDFsAndZip(printConfig) {
  return async (dispatch, getState) => {
    try {
      const state = getState();
      const surveySubmissions = state.collapsibleTableChart.surveySubmissions;
      const results = surveySubmissions?.results || [];
      const usernames = [...new Set(results.map((result) => result.lastEditedBy).filter(Boolean))];
      const employeeNumbers = new Map();

      await Promise.all(
        usernames.map(async (username) => {
          try {
            const user = await rest.doGet(`/admin/users?username=${username}`);
            employeeNumbers.set(username, user?.employeeNumber || "");
          } catch (error) {
            employeeNumbers.set(username, "");
          }
        })
      );

      const submissionsWithEmployeeNumbers = {
        ...surveySubmissions,
        results: results.map((result) => {
          const username = result.lastEditedBy;
          return { ...result, employeeNumber: employeeNumbers.get(username) || "" };
        })
      };

      await PDFCreator.createPDFForGivenTemplates(
        submissionsWithEmployeeNumbers,
        state.collapsibleTableChart.templates,
        printConfig
      );
    } finally {
      dispatch(endLoading());
      dispatch(removePDFConversionData());
    }
  };
}

function loadSuccessful(data, actionType) {
  return {
    [TYPE_KEY]: actionType,
    [PROPERTY_VALUE]: data
  };
}

export function removePDFConversionData() {
  return {
    [TYPE_KEY]: REMOVE_PDF_CONVERSION_DATA
  };
}

function getTimeFacetParameterString(selectedTimeFacets) {
  let url = "";
  if (selectedTimeFacets && selectedTimeFacets.length > 0) {
    selectedTimeFacets.forEach((facet, index) => {
      url += `&timeFacets[${index}].from=${facet.startTime}&timeFacets[${index}].to=${facet.endTime}`;
    });
  }
  return url;
}

function getQuestionKeysParameterString(questionKeys) {
  let url = "";
  if (questionKeys && questionKeys.length > 0) {
    questionKeys.forEach((questionKey) => {
      url += `&questionKeys=${encodeURIComponent(questionKey)}`;
    });
  }
  return url;
}
